/**
 * The two turns-per-day representations a `WinderEntry` can carry, and the rule
 * that only one of them is ever written.
 *
 * `turnsPerDay` is a list of the discrete settings a winder offers
 * (`[650, 750, 900, 1200]`). `turnsPerDayRange` is the alternative shape for a
 * programmable winder that runs anywhere between two bounds. Shared-types
 * declares both as optional and permits both — a type cannot express "exactly
 * one of these", and existing documents predate the range — so the mutual
 * exclusion is enforced HERE, on the write path, and the editor's toggle is the
 * UI expression of the same rule.
 *
 * Its own module rather than a private helper in firebase.ts, following
 * `winderDocId.ts`: this is the thing worth testing about the write, and testing
 * it should not mean standing up the Firebase SDK.
 */

/** A continuous turns-per-day span, as stored. */
export interface TurnsPerDayRange {
  min: number;
  max: number;
}

/** What the editor is currently expressing. */
export type TurnsPerDayMode = 'list' | 'range';

/** Both TPD fields as `saveWinder` writes them — at most one is non-null. */
export interface TurnsPerDayWrite {
  turnsPerDay: number[] | null;
  turnsPerDayRange: TurnsPerDayRange | null;
}

/**
 * Positive integers only, deduped and ascending — the order the editor, the
 * admin listing and the RN picker all want to render.
 */
export function normalizeTurnsPerDayList(values?: (number | null)[] | null): number[] {
  return [
    ...new Set(
      (values ?? [])
        .map((n) => (typeof n === 'number' && Number.isFinite(n) ? Math.round(n) : null))
        .filter((n): n is number => n !== null && n > 0),
    ),
  ].sort((a, b) => a - b);
}

/**
 * A usable range, or null.
 *
 * Both bounds must be positive integers, and `min` must not exceed `max` — a
 * reversed pair is a typo, not an instruction to swap, because "1200 to 300" on
 * a detail screen reads as data corruption either way. Equal bounds ARE allowed:
 * a fixed-rate winder legitimately has a one-point range.
 */
export function normalizeTurnsPerDayRange(
  range?: { min?: number | null; max?: number | null } | null,
): TurnsPerDayRange | null {
  if (!range) return null;
  const min = typeof range.min === 'number' && Number.isFinite(range.min)
    ? Math.round(range.min)
    : null;
  const max = typeof range.max === 'number' && Number.isFinite(range.max)
    ? Math.round(range.max)
    : null;
  if (min === null || max === null) return null;
  if (min <= 0 || max <= 0) return null;
  if (min > max) return null;
  return { min, max };
}

/**
 * Resolve the two fields for a write, given which representation the editor is
 * showing.
 *
 * The MODE decides, not the presence of data: a form that was switched from
 * range back to list still holds the old min/max in its inputs, and writing
 * both would leave a document where the consumer rule ("prefer the range")
 * silently wins over the list the admin is looking at. So the inactive
 * representation is written as `null` — cleared on the document, the same
 * contract every other optional winder field has when emptied.
 *
 * A range mode that fails to normalize (half-filled, reversed, zero) clears
 * BOTH rather than falling back to the list: the admin asked for a range, and
 * quietly resurrecting a list they had stopped editing would be a worse
 * surprise than an empty TPD they can see is empty.
 */
export function resolveTurnsPerDayWrite(
  mode: TurnsPerDayMode,
  list?: (number | null)[] | null,
  range?: { min?: number | null; max?: number | null } | null,
): TurnsPerDayWrite {
  if (mode === 'range') {
    return { turnsPerDay: null, turnsPerDayRange: normalizeTurnsPerDayRange(range) };
  }
  const values = normalizeTurnsPerDayList(list);
  return { turnsPerDay: values.length ? values : null, turnsPerDayRange: null };
}

/**
 * Which representation an existing entry is using, for seeding the editor.
 *
 * The range wins when both are somehow present (a document written before this
 * rule existed, or by a script), matching the consumer rule shared-types
 * documents — the editor then shows what the RN app is showing, and the next
 * save clears the loser.
 */
export function turnsPerDayModeOf(entry?: {
  turnsPerDayRange?: { min?: number | null; max?: number | null } | null;
}): TurnsPerDayMode {
  return normalizeTurnsPerDayRange(entry?.turnsPerDayRange) ? 'range' : 'list';
}

/**
 * Display form for a listing cell or a diff column: "300–1200" for a range,
 * "650, 900" for a list, empty string for neither.
 *
 * En dash rather than a hyphen — this is a numeric span, and the admin listing
 * renders it in a narrow column where "300-1200" reads as one hyphenated token.
 */
export function formatTurnsPerDay(entry?: {
  turnsPerDay?: number[] | null;
  turnsPerDayRange?: { min?: number | null; max?: number | null } | null;
}): string {
  const range = normalizeTurnsPerDayRange(entry?.turnsPerDayRange);
  if (range) return `${range.min}–${range.max}`;
  const list = entry?.turnsPerDay;
  return list && list.length ? list.join(', ') : '';
}
